'use client';

import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { ButtonProps } from '@packages/shared/src/ui/Button/Button';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { AddProductToOrderableBasketDirectDocument } from '@packages/gql/generated/shopping/AddProductToOrderableBasketDirectDocument';
import { CheckoutOrderableBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/CheckoutOrderableBasketFragmentFragmentDoc';
import type { CheckoutAddProductToOrderableBasketDirect } from '@packages/gql/generated/shopping/graphql';
import { selectedQuantity } from '../atoms';
import { addToBasketMessages } from '../AddToBasketButton/messages';
import {
  useAddToBasketTracking,
  type UseAddToBasketTrackingProps,
} from '../Tracking/hooks/useAddToBasketTracking/useAddToBasketTracking';

/* GraphQL */ `
  mutation AddProductToOrderableBasketDirect($payload: CheckoutAddProductToOrderableBasketDirect!) {
    addProductToOrderableBasketDirect(payload: $payload) {
      __typename
      ... on CheckoutChangedOrderableBasket {
        success
        orderableBasket {
          ...CheckoutOrderableBasketFragment
        }
      }
      ... on CheckoutServerFailure {
        failureMessage
      }
    }
  }
`;

export type AddProductToOrderableBasketButtonProps = Omit<ButtonProps, 'onClick' | 'disabled'> &
  Pick<CheckoutAddProductToOrderableBasketDirect, 'coupons' | 'loyaltyCoupons'> &
  Pick<UseAddToBasketTrackingProps, 'trackingProductData'> & {
    sku: string;
    onClose: () => void;
  };

export const AddProductToOrderableBasketButton = ({
  sku,
  coupons,
  loyaltyCoupons,
  trackingProductData,
  onClose,
  ...buttonProps
}: AddProductToOrderableBasketButtonProps) => {
  const { formatMessage } = useIntl();
  const { locale } = useI18n();
  const { addNotification } = useNotification();
  const quantity = useAtomValue(selectedQuantity);
  const { trackGlycerinAddToOrderableBasket } = useAddToBasketTracking({ trackingProductData });
  const [{ fetching }, executeAdd] = useAuthMutation(AddProductToOrderableBasketDirectDocument);

  const handleClick = async () => {
    const result = await executeAdd({
      payload: {
        locale,
        coupons,
        loyaltyCoupons,
        productToAdd: { sku, count: quantity, basketOption: [] },
      },
    });

    const resultData = result.data?.addProductToOrderableBasketDirect;
    if (resultData?.__typename === 'CheckoutChangedOrderableBasket' && resultData.success) {
      const { products } = getFragmentData(
        CheckoutOrderableBasketFragmentFragmentDoc,
        resultData.orderableBasket,
      );
      trackGlycerinAddToOrderableBasket({ products });
      onClose();
    } else {
      addNotification({
        severity: 'error',
        message: formatMessage(addToBasketMessages.errorMessage),
        duration: 5000,
      });
    }
  };

  return (
    <Button
      size="large"
      color="primary"
      className="w-full"
      {...buttonProps}
      onClick={handleClick}
      disabled={fetching}
    >
      {fetching ? (
        <CircularProgress className="text-color-secondary size-4" />
      ) : (
        formatMessage(addToBasketMessages.available)
      )}
    </Button>
  );
};
